import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import PrintHeader from '../print/PrintHeader';
import { Branding } from '../../hooks/useBranding';

// ── Types ──────────────────────────────────────────────────────────
export interface VitalsData {
  bp?:     string;  // e.g. "120/80"
  pulse?:  string;  // e.g. "72"
  temp?:   string;  // e.g. "98.4" (°F)
  spo2?:   string;  // e.g. "98"
  weight?: string;  // e.g. "68" (kg)
  height?: string;  // e.g. "170" (cm)
  bmi?:    string;  // e.g. "23.5"
}

export interface BlankRxData {
  hospital: {
    name:       string;
    address:    string;
    phone:      string;
    licenseNo?: string;
    tagline?:   string;
    logoUrl?:   string;
    rxFooter?:  string;
    settings?:  Branding;
  };
  doctor: {
    name:            string;
    qualifications:  string;
    regNo?:          string;
    specialization?: string;
  };
  patient: {
    name:   string;
    uid:    string;
    age?:   number;
    gender: string;
  };
  encounterId: string;
  date?:       string;   // yyyy-MM-dd; defaults to today
  vitals?:     VitalsData;
}

interface Props {
  data:       BlankRxData;
  onDone:     () => void;
  paperSize?: 'A4' | 'A5';
}

// ── QR placeholder (24 × 24 mm) ───────────────────────────────────
function QrPlaceholder({ encounterId }: { encounterId: string }) {
  return (
    <div style={{
      width: '24mm', height: '24mm',
      border: '1.5px solid #9ca3af',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: '2px', background: '#f9fafb', flexShrink: 0,
    }}>
      <svg viewBox="0 0 70 70" width="17mm" height="17mm">
        {/* TL finder */}
        <rect x="0"  y="0"  width="21" height="21" rx="2" fill="#222"/>
        <rect x="3"  y="3"  width="15" height="15" rx="1" fill="#fff"/>
        <rect x="6"  y="6"  width="9"  height="9"  rx="1" fill="#222"/>
        {/* TR finder */}
        <rect x="49" y="0"  width="21" height="21" rx="2" fill="#222"/>
        <rect x="52" y="3"  width="15" height="15" rx="1" fill="#fff"/>
        <rect x="55" y="6"  width="9"  height="9"  rx="1" fill="#222"/>
        {/* BL finder */}
        <rect x="0"  y="49" width="21" height="21" rx="2" fill="#222"/>
        <rect x="3"  y="52" width="15" height="15" rx="1" fill="#fff"/>
        <rect x="6"  y="55" width="9"  height="9"  rx="1" fill="#222"/>
        {/* Timing */}
        {[0,2,4,6,8,10].map(i => (
          <rect key={i} x={21+i*2} y="6" width="2" height="2" fill="#222"/>
        ))}
        {/* Data modules */}
        {([
          [24,24],[28,24],[32,24],[36,24],[40,24],[44,24],
          [24,28],[28,32],[32,28],[36,32],[40,28],[44,32],
          [24,36],[28,36],[32,40],[36,36],[40,40],[44,36],
          [24,44],[32,44],[40,44],[28,40],[44,44],
        ] as [number,number][]).map(([x,y],i) => (
          <rect key={i} x={x} y={y} width="3" height="3" fill="#222"/>
        ))}
      </svg>
      <span style={{
        fontFamily: 'monospace', fontSize: '4pt',
        color: '#6b7280', textAlign: 'center', lineHeight: 1,
      }}>
        {encounterId.slice(-8).toUpperCase()}
      </span>
    </div>
  );
}

// ── Single vital row ───────────────────────────────────────────────
// Uses CSS grid so every row in a column group has identical column widths —
// label column is explicitly sized so underlines start at the same X position.
interface VitalItemProps {
  label:   string;
  unit:    string;
  value?:  string;
  labelW:  string;  // explicit label column width — must be consistent within a group
}

function VitalItem({ label, unit, value, labelW }: VitalItemProps) {
  return (
    <div style={{
      display:             'grid',
      gridTemplateColumns: `${labelW} 21mm max-content`,
      alignItems:          'baseline',
      columnGap:           '2px',
      marginBottom:        '3.5px',
    }}>
      {/* Label */}
      <span style={{
        fontSize:    '7pt',
        color:       '#374151',
        whiteSpace:  'nowrap',
      }}>
        {label}
      </span>

      {/* Value (filled) or blank dotted underline (handwrite) */}
      {value ? (
        <span style={{
          fontSize:     '7.5pt',
          fontWeight:   600,
          color:        '#111827',
          background:   '#f3f4f6',
          borderRadius: '2px',
          padding:      '1px 4px',
          display:      'block',
          textAlign:    'center',
          lineHeight:   1.35,
        }}>
          {value}
        </span>
      ) : (
        <span style={{
          display:      'block',
          borderBottom: '1px dotted #9ca3af',
          height:       '10px',
        }}/>
      )}

      {/* Unit */}
      <span style={{
        fontSize:    '6pt',
        color:       '#9ca3af',
        paddingLeft: '2px',
        whiteSpace:  'nowrap',
      }}>
        {unit}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────
export default function BlankRxPrint({ data, onDone, paperSize = 'A4' }: Props) {
  const { hospital, doctor, patient, encounterId, date, vitals } = data;
  const printDate = date
    ? format(new Date(date), 'dd MMM yyyy')
    : format(new Date(), 'dd MMM yyyy');

  // ── Doctor block visibility ───────────────────────────────────
  // settings.doctor_details_in_header = true  → credentials on pre-printed letterhead
  // settings.doctor_details_in_header = false → render credentials block below header
  const skipDoctorBlock = hospital.settings?.doctor_details_in_header === true;

  // ── Portal target ─────────────────────────────────────────────
  const portalRef = useRef<HTMLDivElement | null>(null);
  if (!portalRef.current) {
    let el = document.getElementById('as-print-portal') as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'as-print-portal';
      document.body.appendChild(el);
    }
    portalRef.current = el;
  }

  // ── Inject dynamic @page size, then trigger print ─────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'as-rx-page';
    style.textContent = `@page { size: ${paperSize} portrait; margin: 0mm !important; }`;
    document.head.appendChild(style);

    const frameId = requestAnimationFrame(() => {
      window.print();
      onDone();
    });
    return () => {
      cancelAnimationFrame(frameId);
      style.remove();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Shared style tokens ───────────────────────────────────────
  const VALUE = { fontSize: '8pt',  color: '#1f2937', fontWeight: 600 as const };
  const LABEL = { fontSize: '7pt',  color: '#6b7280' };
  const SMALL = { fontSize: '7pt',  color: '#374151' };

  // ── Section header label (VITALS / ANTHROPOMETRY) ─────────────
  const sectionCap: React.CSSProperties = {
    fontSize:       '5.5pt',
    fontWeight:     700,
    color:          '#9ca3af',
    textTransform:  'uppercase',
    letterSpacing:  '0.09em',
    marginBottom:   '4px',
  };

  const content = (
    <div style={{
      width: '100%', height: '100vh',
      overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      boxSizing: 'border-box',
      padding: '0 4mm 3mm',
      fontFamily: 'Arial, Helvetica, sans-serif',
      color: '#111', backgroundColor: '#fff',
    }}>

      {/* ── 1. HOSPITAL IDENTITY HEADER ────────────────────────── */}
      {hospital.settings ? (
        <div style={{ flexShrink: 0 }}>
          <PrintHeader
            type="prescription"
            settings={hospital.settings}
            hospitalName={hospital.name}
          />
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          paddingBottom: '5px', borderBottom: '2px solid #1f2937',
          marginBottom: '4px', flexShrink: 0,
        }}>
          <div style={{ flexShrink: 0 }}>
            {hospital.logoUrl ? (
              <img src={hospital.logoUrl} alt="logo"
                   style={{ width: '14mm', height: '14mm', objectFit: 'contain' }}/>
            ) : (
              <div style={{
                width: '14mm', height: '14mm', background: '#1a1a2e',
                borderRadius: '3mm', display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: '#fff', fontSize: '9pt', fontWeight: 700,
              }}>
                {hospital.name.slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '11pt', lineHeight: 1.2 }}>{hospital.name}</div>
            {hospital.tagline && <div style={LABEL}>{hospital.tagline}</div>}
          </div>
        </div>
      )}

      {/* ── 2. DOCTOR CREDENTIALS (conditional) ─────────────────
           Skipped when doctor_details_in_header is true —
           assumes the printed stationery / letterhead image already
           contains the consulting doctor's name, degree, and Reg. No.
           When false / absent, renders an authoritative credentials
           block to satisfy Indian clinical documentation requirements.
      ─────────────────────────────────────────────────────────── */}
      {!skipDoctorBlock && (
        <div style={{
          paddingBottom: '4px',
          borderBottom:  '1.5px solid #1a1a2e',
          marginBottom:  '4px',
          flexShrink:    0,
        }}>
          <div style={{ ...VALUE, fontSize: '9.5pt', marginBottom: '1px' }}>
            {doctor.name}
          </div>
          <div style={{ ...SMALL, lineHeight: 1.4 }}>
            {doctor.qualifications}
            {doctor.specialization ? ` · ${doctor.specialization}` : ''}
          </div>
          {doctor.regNo && (
            <div style={{ ...LABEL, marginTop: '1px' }}>
              Reg. No: {doctor.regNo}
            </div>
          )}
        </div>
      )}

      {/* ── 3. VITALS — 2-column grid + QR code far-right ────────
           Col A: BP / Pulse / Temp / SpO₂  (clinical observations)
           Col B: Weight / Height / BMI      (anthropometry)
           Col C: QR code — pushed to far right via flex spacer,
                  vertically centred to match the vitals block height.

           Each VitalItem uses an explicit-width label column so that
           all underlines within a column group start at the same X
           position, eliminating the horizontal alignment bug.
      ─────────────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        alignItems:   'center',          // vertically centre QR with both vital groups
        gap:          '5mm',
        padding:      '4px 0 5px',
        borderBottom: '1px solid #d1d5db',
        marginBottom: '4px',
        flexShrink:   0,
      }}>

        {/* Col A — clinical observations */}
        <div style={{ flex: '0 0 auto', alignSelf: 'flex-start' }}>
          <div style={sectionCap}>Vitals</div>
          <VitalItem label="BP"    unit="mmHg" value={vitals?.bp}    labelW="30px"/>
          <VitalItem label="Pulse" unit="/min"  value={vitals?.pulse} labelW="30px"/>
          <VitalItem label="Temp"  unit="°F"   value={vitals?.temp}  labelW="30px"/>
          <VitalItem label="SpO₂"  unit="%"    value={vitals?.spo2}  labelW="30px"/>
        </div>

        {/* Vertical rule */}
        <div style={{
          width:      '1px',
          background: '#e5e7eb',
          alignSelf:  'stretch',
          flexShrink: 0,
          margin:     '0 1mm',
        }}/>

        {/* Col B — anthropometry */}
        <div style={{ flex: '0 0 auto', alignSelf: 'flex-start' }}>
          <div style={sectionCap}>Anthropometry</div>
          <VitalItem label="Weight" unit="kg" value={vitals?.weight} labelW="42px"/>
          <VitalItem label="Height" unit="cm" value={vitals?.height} labelW="42px"/>
          <VitalItem label="BMI"    unit=""   value={vitals?.bmi}    labelW="42px"/>
        </div>

        {/* Flex spacer — pushes QR to far right */}
        <div style={{ flex: 1 }}/>

        {/* QR code — right-aligned, vertically centred */}
        <div style={{
          flexShrink: 0,
          display:    'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap:        '2px',
        }}>
          <QrPlaceholder encounterId={encounterId}/>
          <span style={{
            fontSize:  '5pt',
            color:     '#9ca3af',
            textAlign: 'center',
            lineHeight: 1.2,
          }}>
            Encounter ID
          </span>
        </div>
      </div>

      {/* ── 4. PATIENT DEMOGRAPHICS BAR ──────────────────────────
           Distinct typographic separation from the vitals section:
           solid top border + shaded background + defined vertical
           padding ensures the patient identity row reads as a clear
           independent band before the prescription writing area.
      ─────────────────────────────────────────────────────────── */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        flexWrap:     'wrap',
        gap:          '12px',
        padding:      '5px 8px',
        background:   '#f3f4f6',
        border:       '1px solid #e5e7eb',
        borderRadius: '3px',
        marginBottom: '5px',
        flexShrink:   0,
        fontSize:     '8pt',
        color:        '#374151',
      }}>
        <span><span style={LABEL}>Patient: </span><strong>{patient.name}</strong></span>
        <span>
          <span style={LABEL}>Age/Sex: </span>
          <strong>
            {patient.age != null ? `${patient.age}y` : '—'}
            {' '}{patient.gender === 'male' ? 'M' : patient.gender === 'female' ? 'F' : 'O'}
          </strong>
        </span>
        <span><span style={LABEL}>Date: </span><strong>{printDate}</strong></span>
        <span>
          <span style={LABEL}>ID: </span>
          <strong style={{ fontFamily: 'monospace' }}>{patient.uid}</strong>
        </span>
      </div>

      {/* ── 5. PRESCRIPTION WRITING AREA (flex:1 fills remaining page) */}
      <div style={{
        flex:      '1 1 0',
        overflow:  'hidden',
        display:   'flex',
        flexDirection: 'column',
        paddingTop: '4px',
      }}>
        <div style={{
          display:     'flex',
          alignItems:  'baseline',
          gap:         '8px',
          marginBottom: '4px',
          flexShrink:  0,
        }}>
          <span style={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize:   '20pt',
            color:      '#374151',
            lineHeight: 1,
          }}>℞</span>
          <span style={{ fontSize: '7pt', color: '#9ca3af', fontStyle: 'italic' }}>
            (write medicines, dosage, and instructions below)
          </span>
        </div>
        <div style={{ flex: 1, background: '#fff' }}/>
      </div>

      {/* ── 6. FOOTER ──────────────────────────────────────────── */}
      <div style={{
        borderTop:  '1px solid #9ca3af',
        paddingTop: '4px',
        marginTop:  '3px',
        flexShrink: 0,
        fontSize:   '6.5pt',
        color:      '#6b7280',
      }}>
        {hospital.rxFooter && (
          <div style={{ color: '#4b5563', marginBottom: '2px' }}>{hospital.rxFooter}</div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
          <span style={{
            maxWidth: '75mm', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {hospital.address}
          </span>
          <span style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
            {hospital.phone     && <span>&#9990;&nbsp;{hospital.phone}</span>}
            {hospital.licenseNo && <span>Lic:&nbsp;{hospital.licenseNo}</span>}
          </span>
        </div>
      </div>

    </div>
  );

  return createPortal(content, portalRef.current);
}
